import React, { useEffect, useMemo, useState } from 'react';
import { LogOut, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from './ImageUpload';

/**
 * Feb 2026 — OffDutyScreen
 *
 * Full-page gate rendered when a non-super admin is outside their
 * configured duty window (day OR time). All computations run in
 * UTC+4 (Asia/Muscat, no DST) — the office timezone. The clock
 * ticks every second so the user sees exactly when their next
 * shift is due to begin.
 */

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const OFFICE_TZ_OFFSET_MS = 4 * 60 * 60 * 1000; // UTC+4, no DST

// Convert a wall-clock Date to an office-timezone view via a shifted
// UTC Date. The returned Date's `getUTCXxx` accessors correspond to
// the office-timezone wall-clock values.
const toOfficeDate = (d = new Date()) => new Date(d.getTime() + OFFICE_TZ_OFFSET_MS);

// Parse "HH:MM" → minutes since midnight (returns null on bad input).
const parseHHMM = (s) => {
  if (!s || typeof s !== 'string') return null;
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// JavaScript's `getDay()` returns 0=Sun..6=Sat. Convert to ISO where
// 0=Mon..6=Sun so it matches the backend `duty_days` convention.
const isoDayOfWeek = (d) => (d.getUTCDay() + 6) % 7;

const formatOfficeTime = (d) => {
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const s = d.getUTCSeconds();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} ${ampm}`;
};

const formatHHMMToAMPM = (hhmm) => {
  const mins = parseHHMM(hhmm);
  if (mins === null) return hhmm || '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

/**
 * Given the office-timezone `now`, the duty window, the set of
 * allowed weekdays, AND an optional list of hard-lockout weekdays
 * (`off_duty_days`), return either { onDuty: true } or an object
 * describing the NEXT allowed shift start. When today falls in
 * `off_duty_days` the reason is stamped as `'off_day'` so the
 * lockout screen can render a scheduled-off message instead of
 * the standard off-hours copy.
 */
export const computeDutyStatus = (nowOffice, dutyStart, dutyEnd, dutyDays, offDutyDays) => {
  const startMin = parseHHMM(dutyStart);
  const endMin = parseHHMM(dutyEnd);
  const days = Array.isArray(dutyDays) ? [...dutyDays].sort((a, b) => a - b) : [];
  const offDays = Array.isArray(offDutyDays) ? [...offDutyDays].sort((a, b) => a - b) : [];
  const today = isoDayOfWeek(nowOffice);
  // Feb 2026 — Off-Duty Days lockout wins over every other rule.
  // Compute the next non-off-duty allowed shift so the copy can
  // point users at "Back on duty on <day> at <time>".
  if (offDays.includes(today)) {
    return {
      onDuty: false,
      reason: 'off_day',
      ..._computeNextShift(nowOffice, startMin, days, offDays, /*skipTodayEarlyReturn*/ true),
    };
  }
  // If either the window or the day list is missing/empty, treat as
  // "no gate" — the caller should have already skipped rendering us
  // in that case; guard here so we never hard-lock a mis-configured
  // user out of their own tools.
  if (startMin === null || endMin === null || days.length === 0) {
    return { onDuty: true };
  }
  const nowMin = nowOffice.getUTCHours() * 60 + nowOffice.getUTCMinutes();
  const withinToday = days.includes(today) && nowMin >= startMin && nowMin < endMin;
  if (withinToday) return { onDuty: true };
  return {
    onDuty: false,
    reason: 'off_hours',
    ..._computeNextShift(nowOffice, startMin, days, offDays, /*skipTodayEarlyReturn*/ false),
  };
};

// ---------------------------------------------------------------------
// Helper — find the next Muscat-local shift start that is on a
// permitted `duty_days` weekday AND not blocked by `off_duty_days`.
// Returns partial status describing the next slot (or nothing when
// nothing is scheduled).
// ---------------------------------------------------------------------
function _computeNextShift(nowOffice, startMin, days, offDays, skipTodayEarlyReturn) {
  if (startMin === null || days.length === 0) return {};
  const today = isoDayOfWeek(nowOffice);
  const nowMin = nowOffice.getUTCHours() * 60 + nowOffice.getUTCMinutes();
  for (let offset = 0; offset < 8; offset += 1) {
    const candidateDay = (today + offset) % 7;
    if (!days.includes(candidateDay)) continue;
    if (offDays.includes(candidateDay)) continue;
    if (offset === 0) {
      if (!skipTodayEarlyReturn && nowMin < startMin) {
        const nextDate = new Date(nowOffice.getTime());
        nextDate.setUTCHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
        return {
          nextDay: DAY_LABELS[candidateDay],
          nextStart: `${String(Math.floor(startMin / 60)).padStart(2, '0')}:${String(startMin % 60).padStart(2, '0')}`,
          nextDateOffice: nextDate,
          relative: 'later today',
        };
      }
      continue;
    }
    const nextDate = new Date(nowOffice.getTime());
    nextDate.setUTCDate(nowOffice.getUTCDate() + offset);
    nextDate.setUTCHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
    return {
      nextDay: DAY_LABELS[candidateDay],
      nextStart: `${String(Math.floor(startMin / 60)).padStart(2, '0')}:${String(startMin % 60).padStart(2, '0')}`,
      nextDateOffice: nextDate,
      relative: offset === 1 ? 'tomorrow' : `in ${offset} days`,
    };
  }
  return {};
}

/**
 * Feb 2026 — Compute whether an approved overtime slot is currently
 * live, based on the server-supplied `{date, start_time, end_time}`
 * and the wall-clock office time. Kept as a pure helper so DutyGate
 * can re-evaluate every 30s without hitting the network.
 */
export const isOvertimeWindowLive = (window, nowOffice = toOfficeDate(new Date())) => {
  if (!window || typeof window !== 'object') return false;
  const date = (window.date || '').trim();
  const start = parseHHMM(window.start_time);
  const end = parseHHMM(window.end_time);
  if (!date || start === null || end === null || end <= start) return false;
  const y = nowOffice.getUTCFullYear();
  const m = nowOffice.getUTCMonth() + 1;
  const d = nowOffice.getUTCDate();
  const todayISO = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  if (todayISO !== date) return false;
  const nowMin = nowOffice.getUTCHours() * 60 + nowOffice.getUTCMinutes();
  return nowMin >= start && nowMin < end;
};

const OffDutyScreen = ({ dutyMeta, onLogout }) => {
  const { settings } = useSettings();
  const logoUrl = resolveImageUrl(settings?.logo_image || '');
  const [nowTick, setNowTick] = useState(() => new Date());

  // Ticking clock — refresh every second so the countdown feels alive.
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const nowOffice = useMemo(() => toOfficeDate(nowTick), [nowTick]);
  const status = useMemo(
    () => computeDutyStatus(
      nowOffice,
      dutyMeta?.duty_start,
      dutyMeta?.duty_end,
      dutyMeta?.duty_days,
      dutyMeta?.off_duty_days,
    ),
    [nowOffice, dutyMeta],
  );

  // Feb 2026 — Note: we intentionally do NOT reload the page when
  // the LOCAL clock says the shift just started. The server's
  // `on_duty` flag on /admin/me is the sole gate; DutyGate polls
  // /admin/me every 60s (and on tab focus) and drops OffDutyScreen
  // the moment the server flips the flag. A wrong local clock only
  // affects the visual countdown label, never access.

  const isOffDay = status.reason === 'off_day';

  const nextShiftLabel = status.onDuty
    ? ''
    : status.nextDay && status.nextStart
      ? `${status.relative === 'later today' ? 'Later today' : status.nextDay} at ${formatHHMMToAMPM(status.nextStart)}`
      : 'Not scheduled — contact your super admin.';

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#0B1220] to-[#020617] p-6"
      data-testid="off-duty-screen"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        <div
          className={`px-6 py-8 flex flex-col items-center gap-3 ${isOffDay ? 'bg-amber-500' : 'bg-[#0078D4]'}`}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              className="h-14 w-auto max-w-[220px] object-contain"
              data-testid="off-duty-logo"
            />
          ) : (
            <div className="h-14 w-40 rounded bg-white/10" aria-hidden />
          )}
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/80 font-bold">
            {isOffDay ? 'Scheduled off-day' : 'Access control'}
          </div>
        </div>
        <div className="px-6 py-6 space-y-4">
          <h1
            className="text-2xl font-extrabold text-[#0F172A] leading-tight"
            data-testid="off-duty-title"
          >
            {isOffDay ? 'Today is a scheduled off-day.' : 'You are currently off-duty.'}
          </h1>
          <p
            className="text-sm text-neutral-600 leading-relaxed"
            data-testid="off-duty-subtitle"
          >
            {isOffDay ? (
              <>Today is a scheduled off-day for this account
                {dutyMeta?.name ? <>, <b>{dutyMeta.name}</b></> : null}. Access will resume automatically on the next working day.</>
            ) : (
              <>Your account is outside its assigned duty window
                {dutyMeta?.name ? <>, <b>{dutyMeta.name}</b></> : null}. Access to the workspace will resume automatically at your next scheduled shift.</>
            )}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Current time (UTC+4)
              </div>
              <div className="mt-1 text-xl font-extrabold text-[#0F172A] tabular-nums" data-testid="off-duty-current-time">
                {formatOfficeTime(nowOffice)}
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${isOffDay ? 'border-amber-200 bg-amber-50/60' : 'border-sky-200 bg-sky-50/60'}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${isOffDay ? 'text-amber-800' : 'text-sky-800'}`}>
                <CalendarIcon className="w-3 h-3" /> {isOffDay ? 'Back on duty' : 'Next shift starts'}
              </div>
              <div className={`mt-1 text-base font-extrabold ${isOffDay ? 'text-amber-900' : 'text-sky-900'}`} data-testid="off-duty-next-shift">
                {nextShiftLabel}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-full mt-2 h-11 rounded-full bg-[#0F172A] hover:bg-[#020617] text-white text-sm font-bold flex items-center justify-center gap-2 transition"
            data-testid="off-duty-logout"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
          <p className="text-[10px] text-neutral-400 text-center leading-relaxed">
            All times shown in office local time (UTC+4). If you believe your schedule is incorrect, contact your super admin.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Convenience wrapper that reads `dutyMeta` off the auth context,
 * decides whether to gate, and either renders the children or the
 * `OffDutyScreen`. Super admins ALWAYS pass through.
 *
 * Feb 2026 — The gate now trusts the SERVER's `on_duty` flag on
 * `dutyMeta` (computed against Muscat wall-clock time in FastAPI)
 * instead of recomputing on/off from the viewer's system clock.
 * That closes the loophole where a user with a wrong local clock
 * could keep the panel unlocked past their shift. The visual
 * countdown timer inside `OffDutyScreen` still consumes the
 * browser clock — a wrong clock only affects the countdown label,
 * never access.
 */
export const DutyGate = ({ children }) => {
  const { isSuperAdmin, dutyMeta, logout } = useAdminAuth();
  // Re-tick every 30s so the /admin/me poll's next round trip is
  // reflected promptly. The gate itself does NOT recompute on/off
  // locally — this tick just keeps React re-rendering while the
  // 60s poll refreshes `dutyMeta.on_duty` in the background.
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  if (isSuperAdmin) return children;
  // While /admin/me is loading (first fetch has never completed),
  // don't flash the gate.
  if (!dutyMeta) return children;
  // Feb 2026 — Fail CLOSED on repeated /admin/me errors. If the
  // last fetch failed we cannot trust the duty flags, so lock the
  // shell rather than leaking the dashboard behind a stale one.
  // Belt-and-braces: super admin already returned above at line
  // 299, but we double-check here so a future refactor that reorders
  // these checks doesn't accidentally lock qayum@ out on a flaky Wi-Fi.
  if (dutyMeta.fetch_failed && !isSuperAdmin) {
    return <OffDutyScreen dutyMeta={dutyMeta} onLogout={logout} />;
  }
  // Server-authoritative on-duty flag. When present, it wins over
  // everything else — the server has already OR'd the duty window
  // with the overtime slot in Muscat time.
  if (dutyMeta.on_duty === true) return children;
  if (dutyMeta.on_duty === false) {
    return <OffDutyScreen dutyMeta={dutyMeta} onLogout={logout} />;
  }
  // Legacy fallback (a backend that predates the `on_duty` field):
  // preserve the old client-side computation so old deploys don't
  // break. New deploys always hit the `on_duty` branch above.
  const hasWindow = dutyMeta.overtime_window
    && typeof dutyMeta.overtime_window === 'object'
    && dutyMeta.overtime_window.date
    && dutyMeta.overtime_window.end_time;
  if (hasWindow) {
    if (isOvertimeWindowLive(dutyMeta.overtime_window)) return children;
  } else if (dutyMeta.in_overtime_window) {
    return children;
  }
  const status = computeDutyStatus(
    toOfficeDate(new Date()),
    dutyMeta.duty_start,
    dutyMeta.duty_end,
    dutyMeta.duty_days,
    dutyMeta.off_duty_days,
  );
  if (status.onDuty) return children;
  return <OffDutyScreen dutyMeta={dutyMeta} onLogout={logout} />;
};

export default OffDutyScreen;
